import { css } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';

import type IQuantityStyleOptions from '../../types/IQuantityStyleOptions.js';

/**
 * Quantity style.
 */
export default class QuantityStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options object.
	 * @param options.isDisabled Disabled.
	 * @param options.position Position.
	 * @param options.direction Direction.
	 * @param options.theme Theme.
	 * @returns Styles.
	 */
	public static getStyle(options: IQuantityStyleOptions): string {
		return css`
			${FontStyle.getStyle({
				type: ThemeFontStyleTypeEnum.bodyMedium,
				theme: options.theme
			})}
			${this.getHover(options)}
			${this.getTypeStyle(options)}
			${this.getRtlStyle(options)}
			${this.getFocusStyle(options)}
			${options.isDisabled ? this.getDisabled(options) : ''}
			${this.getPrintStyle()}
		`;
	}

	/**
	 * @param options Options object.
	 * @param options.theme Theme.
	 * @param options.position Position.
	 * @param options.isDisabled ...
	 * @returns Styles.
	 */
	private static getTypeStyle(options: IQuantityStyleOptions): string {
		const { color, border, spacing, motion } = options.theme;
		return css`
			:host {
				display: flex;
				flex-direction: column;
			}

			select {
				display: block;
				-webkit-appearance: none;
				-moz-appearance: none;
				appearance: none;
				margin: 0;
				outline: 0;
				text-indent: 0;
				color: ${color.text.dark};
				width: ${90 / 16}rem;
				padding: ${spacing.space50 / 16}rem ${36 / 16}rem ${spacing.space50 / 16}rem
					${spacing.space125 / 16}rem;
				background: ${color.neutral.white};
				-moz-border-radius: ${border.radius.radius64 / 16}rem;
				-webkit-border-radius: ${border.radius.radius64 / 16}rem;
				border-radius: ${border.radius.radius64 / 16}rem;
				border: ${1 / 16}rem solid ${color.border.light};
				line-height: ${22 / 16}rem;
				font-size: inherit;
				transition-property: color, border, transform;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
			}
			select:active {
				border-color: ${color.border.dark};
			}
			select:focus {
				border-color: ${color.border.dark};
			}
			kompis-icon {
				position: absolute;
				display: inline-flex;
				top: calc(50% - ${3 / 16}rem);
				right: ${spacing.space125 / 16}rem;

				pointer-events: none;
				transition-property: color, transform;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
				color: ${color.icon.default};
			}
			.container {
				display: flex;
			}
			.dropdown {
				position: relative;
				display: inline-flex;
			}
			.dropdown:focus {
				outline: none;
			}
			.outline {
				position: absolute;
				left: 0;
				top: 0;
				right: 0;
				bottom: 0;
				z-index: inherit;
				border-radius: ${border.radius.radius64 / 16}rem;
				pointer-events: none;
				transition-property: top, left, right, bottom, opacity;
				transition-duration: ${motion.duration.small};
				opacity: ${options.hasFocus ? 1 : 0};
			}
		`;
	}

	/**
	 * Returns hover effect.
	 *
	 * @param options Options object.
	 * @param options.theme Theme.
	 * @returns Css code.
	 */
	private static getHover(options: IQuantityStyleOptions): string {
		const { color } = options.theme;
		if (options.hasFocus) {
			return '';
		}
		return css`
			@media (pointer: fine) {
				select:hover {
					border-color: ${color.neutral.grey500};
				}
			}
		`;
	}

	private static getRtlStyle(options: IQuantityStyleOptions): string {
		const { spacing } = options.theme;
		const { direction } = options;
		if (direction === 'rtl') {
			return css`
				:host kompis-icon {
					left: ${spacing.space125 / 16}rem;
					right: auto;
				}
				:host select {
					padding: ${spacing.space50 / 16}rem ${spacing.space125 / 16}rem ${spacing.space50 / 16}rem
						${36 / 16}rem;
				}
				:host-context kompis-icon {
					left: ${spacing.space125 / 16}rem;
					right: auto;
				}
				:host-context select {
					padding: ${spacing.space50 / 16}rem ${spacing.space125 / 16}rem ${spacing.space50 / 16}rem
						${36 / 16}rem;
				}
			`;
		}
		return css`
			:host(:dir(rtl)) kompis-icon {
				right: auto;
				left: ${spacing.space125 / 16}rem;
			}
			:host(:dir(rtl)) select {
				padding: ${spacing.space50 / 16}rem ${spacing.space125 / 16}rem ${spacing.space50 / 16}rem
					${36 / 16}rem;
				direction: rtl;
			}
			:host-context([dir='rtl']) kompis-icon {
				right: auto;
				left: ${spacing.space125 / 16}rem;
			}
			:host-context([dir='rtl']) select {
				padding: ${spacing.space50 / 16}rem ${spacing.space125 / 16}rem ${spacing.space50 / 16}rem
					${36 / 16}rem;
				direction: rtl;
			}
		`;
	}

	private static getDisabled(options: IQuantityStyleOptions): string {
		const { color } = options.theme;
		return css`
			select {
				border-color: ${color.neutral.grey200};
				color: ${color.neutral.grey200};
				pointer-events: none;
			}
			kompis-icon {
				color: ${color.neutral.grey200};
			}
		`;
	}

	/**
	 * Returns the style for focusing the button.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getFocusStyle(options: IQuantityStyleOptions): string {
		if (!options.hasFocus) {
			return '';
		}
		return css`
			${this.getOutlineStyle(options)}
		`;
	}

	/**
	 * Returns the outline style when focusing.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getOutlineStyle(options: IQuantityStyleOptions): string {
		const { focusOutline } = options.theme;
		if (!options.hasFocus) {
			return '';
		}
		return css`
			.outline {
				box-shadow: 0 0 0 0.1rem ${focusOutline.color};
				left: -${focusOutline.offset / 16}rem;
				top: -${focusOutline.offset / 16}rem;
				right: -${focusOutline.offset / 16}rem;
				bottom: -${focusOutline.offset / 16}rem;
			}
		`;
	}

	/**
	 * Returns the outline style when focusing.
	 *
	 * @returns Styles.
	 */
	private static getPrintStyle(): string {
		return css`
			@media print {
				select,
				select:active,
				select:hover,
				select:focus {
					border: 0;
					font-weight: bold;
					padding: 0;
					width: auto;
					flex-direction: column;
					align-items: flex-end;
					display: flex;
				}

				:host(:dir(rtl)) select {
					padding: 0;
				}

				:host-context([dir='rtl']) select {
					padding: 0;
				}

				.outline,
				kompis-icon,
				select * {
					display: none;
				}

				.dropdown:after {
					content: 'x';
					font-weight: bold;
				}
			}
		`;
	}
}
